import {
  YES_NO,
  applicantSection,
  otherAttachments,
  penaltyDeclaration,
  proxyAttachments,
  replySection,
  representationSection,
} from "modules/ebiForms/common";
import { bankAccountField, childAddressFields, childIdentityFields } from "modules/ebiForms/definitions/sharedEducationSafety";
import { EbiCondition, EEbiFormCategory, EEbiFormOffice, IEbiFormDefinition } from "modules/ebiForms/typings";

/**
 * D5. Wniosek o przyznanie stypendium szkolnego albo zasiłku szkolnego — art. 90b–90f i 90m–90o ustawy o systemie
 * oświaty; regulamin udzielania pomocy materialnej uchwala rada gminy (art. 90f). Brak wzoru ogólnopolskiego; układ jak
 * wniosek o stypendium szkolne 2025/2026 opublikowany na samorzad.gov.pl (MOPS w Mrągowie,
 * https://samorzad.gov.pl/attachment/f93c5fd4-19db-4bb7-8f83-1a9a7f619b9d — pobrany 2026-10-22): wnioskodawca, dane
 * ucznia, sytuacja w rodzinie, pożądana forma stypendium, gospodarstwo domowe i dochody, inne stypendia, rachunek,
 * wyjaśnienie złożenia po terminie. Rodzaj świadczenia jest wariantem wniosku w BE. Oświadczenia o dochodach z klauzulą
 * karną (art. 90n ust. 5a). Okoliczności dotyczące zdrowia (niepełnosprawność, choroba, uzależnienia) — tylko jako
 * załączniki. Kwoty kryterium dochodowego nie wpisano: katalog podaje 1010 zł, wzór z Mrągowa 823 zł (art. 8 ust. 1
 * pkt 2 ustawy o pomocy społecznej) — do potwierdzenia z ośrodkiem.
 */
const SCHOLARSHIP: EbiCondition = { field: "swiadczenie", equals: "stypendium" };
const ALLOWANCE: EbiCondition = { field: "swiadczenie", equals: "zasilek" };
const SOCIAL_BENEFITS: EbiCondition = { all: [SCHOLARSHIP, { field: "swiadczeniaPomocySpolecznej", equals: "tak" }] };

const D5: IEbiFormDefinition = {
  code: "D5",
  version: 1,
  title: "Wniosek o przyznanie stypendium szkolnego lub zasiłku szkolnego",
  category: EEbiFormCategory.Education,
  office: EEbiFormOffice.MunicipalUnit,
  description: "Pomoc materialna o charakterze socjalnym dla ucznia: stypendium szkolne albo zasiłek szkolny po zdarzeniu losowym.",
  keywords: ["stypendium szkolne", "zasiłek szkolny", "pomoc materialna", "uczeń", "szkoła", "zdarzenie losowe"],
  organ: "Wójt / Burmistrz / Prezydent Miasta (decyzję wydaje zwykle z upoważnienia ośrodek pomocy społecznej lub centrum usług społecznych)",
  legalBasis: "art. 90b, 90d, 90e, 90m i 90n ustawy z dnia 7 września 1991 r. o systemie oświaty; regulamin udzielania pomocy materialnej uchwalony przez radę gminy",
  request: "Wnoszę o przyznanie świadczenia pomocy materialnej o charakterze socjalnym wskazanego we wniosku — stypendium szkolnego albo zasiłku szkolnego.",
  feeLabel: "bez opłaty",
  fee: "Bez opłaty (sprawy świadczeń socjalnych — art. 2 ust. 1 pkt 1 lit. c ustawy o opłacie skarbowej); pełnomocnictwo — 17 zł.",
  deadlineLabel: "do miesiąca",
  deadline: "Do miesiąca, w sprawach szczególnie skomplikowanych do 2 miesięcy (art. 35 KPA).",
  variantField: "swiadczenie",
  variants: {
    stypendium: { title: "Wniosek o przyznanie stypendium szkolnego" },
    zasilek: { title: "Wniosek o przyznanie zasiłku szkolnego" },
  },
  sections: [
    {
      code: "rodzaj",
      title: "Rodzaj świadczenia",
      fields: [
        {
          code: "swiadczenie",
          type: "radio",
          label: "Wnoszę o przyznanie",
          required: true,
          defaultValue: "stypendium",
          options: [
            { value: "stypendium", label: "stypendium szkolnego (art. 90d) — uczeń w trudnej sytuacji materialnej wynikającej z niskich dochodów na osobę w rodzinie" },
            { value: "zasilek", label: "zasiłku szkolnego (art. 90e) — uczeń przejściowo w trudnej sytuacji materialnej z powodu zdarzenia losowego" },
          ],
        },
        {
          code: "infoTerminStypendium",
          type: "info",
          label:
            "Wniosek o stypendium szkolne składa się do 15 września danego roku szkolnego (słuchacze kolegiów pracowników służb społecznych — do 15 października, uczniowie szkół, w których zajęcia rozpoczynają się w lutym — do 15 lutego). Po terminie wniosek można złożyć w uzasadnionych przypadkach — wyjaśnij przyczynę w części „Oświadczenia”.",
          visibleWhen: SCHOLARSHIP,
        },
        {
          code: "infoTerminZasilek",
          type: "info",
          label: "O zasiłek szkolny można się ubiegać w terminie nie dłuższym niż 2 miesiące od wystąpienia zdarzenia losowego.",
          visibleWhen: ALLOWANCE,
        },
        {
          code: "wnioskodawcaRola",
          type: "radio",
          label: "Składam wniosek jako",
          required: true,
          defaultValue: "rodzic",
          options: [
            { value: "rodzic", label: "rodzic lub opiekun prawny ucznia" },
            { value: "pelnoletniUczen", label: "pełnoletni uczeń (słuchacz kolegium)" },
            { value: "dyrektor", label: "dyrektor szkoły, ośrodka lub kolegium" },
          ],
        },
      ],
    },
    applicantSection(),
    representationSection(),
    {
      code: "uczen",
      title: "Dane ucznia",
      fields: [
        ...childIdentityFields("uczen"),
        { code: "klasa", type: "text", label: "Klasa (semestr)", width: "half", maxLength: 20 },
        ...childAddressFields("uczen", "Uczeń"),
        { code: "szkola", type: "text", label: "Nazwa i adres szkoły, ośrodka lub kolegium", required: true, maxLength: 300 },
        {
          code: "typSzkoly",
          type: "select",
          label: "Typ szkoły",
          required: true,
          options: [
            { value: "podstawowa", label: "szkoła podstawowa" },
            { value: "branzowa", label: "szkoła branżowa" },
            { value: "technikum", label: "technikum" },
            { value: "liceum", label: "liceum ogólnokształcące" },
            { value: "policealna", label: "szkoła policealna" },
            { value: "kolegium", label: "kolegium pracowników służb społecznych" },
            { value: "osrodek", label: "ośrodek rewalidacyjno-wychowawczy" },
          ],
        },
      ],
    },
    {
      code: "sytuacja",
      title: "Sytuacja rodziny i forma stypendium",
      visibleWhen: SCHOLARSHIP,
      fields: [
        {
          code: "okolicznosci",
          type: "checkboxes",
          label: "W rodzinie występuje (zaznacz właściwe i udokumentuj)",
          hint: "Niepełnosprawność, ciężką lub długotrwałą chorobę albo uzależnienie w rodzinie możesz udokumentować w załącznikach — formularz nie pyta o dane o zdrowiu.",
          options: [
            { value: "bezrobocie", label: "bezrobocie" },
            { value: "wielodzietnosc", label: "wielodzietność" },
            { value: "brakUmiejetnosci", label: "brak umiejętności wykonywania funkcji opiekuńczo-wychowawczych" },
            { value: "rodzinaNiepelna", label: "rodzina niepełna" },
            { value: "zdarzenieLosowe", label: "zdarzenie losowe" },
          ],
        },
        {
          code: "formaStypendium",
          type: "checkboxes",
          label: "Pożądana forma stypendium szkolnego",
          required: true,
          options: [
            {
              value: "zajecia",
              label:
                "całkowite lub częściowe pokrycie kosztów udziału w zajęciach edukacyjnych, w tym wyrównawczych, wykraczających poza zajęcia realizowane w szkole w ramach planu nauczania, a także udziału w zajęciach edukacyjnych realizowanych poza szkołą",
            },
            { value: "rzeczowa", label: "pomoc rzeczowa o charakterze edukacyjnym, w tym w szczególności zakup podręczników" },
            {
              value: "naukaPozaMiejscem",
              label: "całkowite lub częściowe pokrycie kosztów związanych z pobieraniem nauki poza miejscem zamieszkania (uczniowie szkół ponadpodstawowych i słuchacze kolegiów)",
            },
            { value: "pieniezna", label: "świadczenie pieniężne — jeżeli udzielenie stypendium w powyższych formach nie jest możliwe lub celowe" },
          ],
        },
        { code: "uzasadnienieFormy", type: "textarea", label: "Uzasadnienie wyboru formy stypendium szkolnego", maxLength: 2000 },
      ],
    },
    {
      code: "zdarzenie",
      title: "Zdarzenie losowe",
      visibleWhen: ALLOWANCE,
      fields: [
        { code: "dataZdarzenia", type: "date", label: "Data wystąpienia zdarzenia losowego", required: true, width: "half" },
        {
          code: "opisZdarzenia",
          type: "textarea",
          label: "Na czym polegało zdarzenie losowe i jak wpłynęło na sytuację materialną ucznia",
          hint: "Krótko, np. „pożar mieszkania — zniszczone podręczniki i przybory”. Dokumentację medyczną dołącz tylko jako załącznik.",
          required: true,
          maxLength: 2000,
        },
        {
          code: "formaZasilku",
          type: "radio",
          label: "Pożądana forma zasiłku szkolnego",
          required: true,
          options: [
            { value: "pieniezna", label: "świadczenie pieniężne na pokrycie wydatków związanych z procesem edukacyjnym" },
            { value: "rzeczowa", label: "pomoc rzeczowa o charakterze edukacyjnym" },
          ],
        },
      ],
    },
    {
      code: "dochody",
      title: "Gospodarstwo domowe i dochody",
      description:
        "Gospodarstwo domowe tworzą osoby spokrewnione lub niespokrewnione, razem mieszkające i gospodarujące. Podaj dochody netto z miesiąca poprzedzającego złożenie wniosku (w razie utraty dochodu — z miesiąca złożenia wniosku).",
      visibleWhen: SCHOLARSHIP,
      fields: [
        {
          code: "czlonkowie",
          type: "repeat",
          label: "Członkowie gospodarstwa domowego ucznia (łącznie z uczniem)",
          itemLabel: "Osoba",
          required: true,
          min: 1,
          max: 15,
          fields: [
            { code: "imieNazwisko", type: "text", label: "Imię i nazwisko", required: true, width: "half", maxLength: 200 },
            { code: "pesel", type: "pesel", label: "PESEL", width: "half", hint: "Jeśli osoba nie ma numeru PESEL — pozostaw puste." },
            { code: "pokrewienstwo", type: "text", label: "Stopień pokrewieństwa z uczniem", required: true, width: "half", maxLength: 50, placeholder: "np. matka, brat, uczeń" },
            { code: "miejscePracyNauki", type: "text", label: "Miejsce pracy lub nauki", width: "half", maxLength: 200 },
          ],
        },
        {
          code: "swiadczeniaPomocySpolecznej",
          type: "radio",
          label: "Czy rodzina ucznia korzysta ze świadczeń pieniężnych z pomocy społecznej?",
          hint: "Jeśli tak, zamiast zaświadczeń o dochodach wystarczy zaświadczenie albo oświadczenie o korzystaniu z tych świadczeń (art. 90n ust. 5).",
          required: true,
          options: YES_NO,
        },
        {
          code: "dochodyCzlonkow",
          type: "repeat",
          label: "Dochody netto członków gospodarstwa domowego",
          itemLabel: "Dochód",
          requiredWhen: { field: "swiadczeniaPomocySpolecznej", equals: "nie" },
          max: 30,
          fields: [
            {
              code: "zrodlo",
              type: "select",
              label: "Źródło dochodu",
              required: true,
              width: "half",
              options: [
                { value: "wynagrodzenie", label: "wynagrodzenie ze stosunku pracy" },
                { value: "emerytura", label: "emerytura, zasiłek przedemerytalny" },
                { value: "renta", label: "renta" },
                { value: "zasilekRodzinny", label: "zasiłek rodzinny i dodatki do zasiłku rodzinnego" },
                { value: "pielegnacyjne", label: "zasiłek pielęgnacyjny, świadczenia pielęgnacyjne" },
                { value: "dodatekMieszkaniowy", label: "dodatek mieszkaniowy" },
                { value: "zasilekOps", label: "zasiłek okresowy, zasiłek stały" },
                { value: "alimenty", label: "alimenty" },
                { value: "bezrobocie", label: "zasiłek dla bezrobotnych, stypendium stażowe" },
                { value: "dzialalnosc", label: "działalność gospodarcza" },
                { value: "umowyCywilne", label: "umowa o dzieło, umowa zlecenia" },
                { value: "gospodarstwoRolne", label: "gospodarstwo rolne" },
                { value: "inne", label: "inne (np. praca dorywcza, praca za granicą)" },
              ],
            },
            { code: "osoba", type: "text", label: "Imię i nazwisko osoby uzyskującej dochód", required: true, width: "half", maxLength: 200 },
            { code: "kwota", type: "number", label: "Kwota netto [zł]", required: true, width: "third", decimals: 2, min: 0 },
          ],
        },
        {
          code: "alimentyNaRzeczInnych",
          type: "number",
          label: "Alimenty świadczone przez członków gospodarstwa na rzecz osób spoza niego w miesiącu poprzedzającym złożenie wniosku [zł]",
          decimals: 2,
          min: 0,
          width: "half",
        },
        {
          code: "inneStypendia",
          type: "radio",
          label: "Czy uczeń otrzymuje lub ubiega się w tym roku szkolnym o inne stypendium o charakterze socjalnym ze środków publicznych?",
          required: true,
          options: YES_NO,
        },
        {
          code: "inneStypendiaOpis",
          type: "text",
          label: "Instytucja, która przyznała stypendium, jego miesięczna wysokość i okres",
          required: true,
          visibleWhen: { field: "inneStypendia", equals: "tak" },
          maxLength: 300,
        },
      ],
    },
    {
      code: "wyplata",
      title: "Wypłata świadczenia",
      fields: [
        bankAccountField("rachunek", "Numer rachunku bankowego do wypłaty (jeśli świadczenie zostanie przyznane w formie pieniężnej)"),
        { code: "rachunekWlasciciel", type: "text", label: "Imię i nazwisko właściciela rachunku", maxLength: 200 },
      ],
    },
    {
      code: "oswiadczenia",
      title: "Oświadczenia",
      fields: [
        { code: "poTerminie", type: "checkbox", label: "Składam wniosek o stypendium szkolne po terminie", visibleWhen: SCHOLARSHIP },
        {
          code: "uzasadnieniePoTerminie",
          type: "textarea",
          label: "Wyjaśnienie złożenia wniosku po terminie",
          required: true,
          visibleWhen: { all: [SCHOLARSHIP, { field: "poTerminie", equals: true }] },
          maxLength: 2000,
        },
        {
          code: "oswiadczenieZgodnosc",
          type: "declaration",
          label: "Oświadczam, że dane podane we wniosku są zgodne ze stanem faktycznym.",
          required: true,
        },
        {
          code: "oswiadczenieDochody",
          type: "declaration",
          label:
            "Oświadczenia o składzie gospodarstwa domowego, wysokości dochodów i korzystaniu ze świadczeń pieniężnych z pomocy społecznej składam pod rygorem odpowiedzialności karnej za składanie fałszywych oświadczeń (art. 90n ust. 5a ustawy o systemie oświaty).",
          required: true,
          visibleWhen: SCHOLARSHIP,
        },
        { ...penaltyDeclaration(), visibleWhen: SCHOLARSHIP },
        {
          code: "oswiadczenieZmiany",
          type: "declaration",
          label: "Zobowiązuję się niezwłocznie powiadomić organ, który przyzna stypendium szkolne, o ustaniu przyczyn, które stanowiły podstawę jego przyznania.",
          required: true,
          visibleWhen: SCHOLARSHIP,
        },
      ],
    },
    replySection("Sposób odbioru decyzji"),
  ],
  attachments: [
    {
      code: "zaswiadczeniaDochody",
      label: "Zaświadczenia albo oświadczenia o dochodach netto członków gospodarstwa domowego z miesiąca poprzedzającego złożenie wniosku",
      hint: "Np. zaświadczenie pracodawcy, decyzja lub odcinek ZUS/KRUS, zaświadczenie urzędu pracy, dokument o alimentach, przy działalności — zaświadczenie lub oświadczenie z urzędu skarbowego, przy gospodarstwie rolnym — dokument o hektarach przeliczeniowych.",
      visibleWhen: SCHOLARSHIP,
      requiredWhen: { field: "swiadczeniaPomocySpolecznej", equals: "nie" },
      multiple: true,
    },
    {
      code: "swiadczeniaOps",
      label: "Zaświadczenie albo oświadczenie o korzystaniu ze świadczeń pieniężnych z pomocy społecznej",
      visibleWhen: SOCIAL_BENEFITS,
      required: true,
    },
    {
      code: "zaswiadczenieSzkoly",
      label: "Zaświadczenie szkoły o uczęszczaniu ucznia (albo oświadczenie)",
      hint: "Wymagane przez część gmin — sprawdź regulamin udzielania pomocy materialnej.",
    },
    {
      code: "dokumentySytuacji",
      label: "Dokumenty potwierdzające szczególną sytuację rodziny",
      hint: "Np. orzeczenie o niepełnosprawności, zaświadczenie lekarskie o ciężkiej lub długotrwałej chorobie, zaświadczenie z urzędu pracy (nieobowiązkowo).",
      visibleWhen: SCHOLARSHIP,
      multiple: true,
    },
    {
      code: "alimentyDokumenty",
      label: "Wyrok zobowiązujący do płacenia alimentów i dowody wpłat",
      hint: "Gdy podajesz alimenty świadczone na rzecz osób spoza gospodarstwa domowego.",
      visibleWhen: { all: [SCHOLARSHIP, { field: "alimentyNaRzeczInnych", filled: true }] },
      multiple: true,
    },
    {
      code: "dokumentZdarzenia",
      label: "Dokument potwierdzający zdarzenie losowe",
      hint: "Np. zaświadczenie straży pożarnej lub policji, akt zgonu, zaświadczenie lekarskie — zwykle wymagany przez ośrodek.",
      visibleWhen: ALLOWANCE,
      multiple: true,
    },
    ...proxyAttachments(),
    otherAttachments(),
  ],
};

export default D5;
